import React, { useState, useEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import Typography from '@mui/material/Typography'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'

import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup from '../../component/BottomButtonGroup'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import { FORM_IMAGE_PREVIEW_FRAME_SX, FORM_PANEL_SX, FORM_SUB_PANEL_SX } from '../../component/formSurfaceStyles'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import {
    applyAppQnaDraft,
    clearAppQnaDraft,
    createAppQnaDraftPayload,
    createEditAppQnaDraftKey,
    readAppQnaDraft,
    saveAppQnaDraft,
} from './appQnaDraftUtils'

const QUESTION_IMAGE_PANEL_SX = {
    ...FORM_SUB_PANEL_SX,
    p: 1.5,
}

const QUESTION_IMAGE_PREVIEW_SX = {
    ...FORM_IMAGE_PREVIEW_FRAME_SX,
    minHeight: 220,
    height: 'auto',
    maxHeight: 520,
}

const QUESTION_IMAGE_PREVIEW_BUTTON_SX = {
    ...QUESTION_IMAGE_PREVIEW_SX,
    p: 0,
    appearance: 'none',
    cursor: 'zoom-in',
    transition: 'border-color 120ms ease, background-color 120ms ease',
    '&:hover': {
        borderColor: 'primary.main',
        backgroundColor: 'action.hover',
    },
    '&:focus-visible': {
        outline: '2px solid',
        outlineColor: 'primary.main',
        outlineOffset: 2,
    },
}

const QUESTION_IMAGE_DIALOG_TITLE_SX = {
    pr: 6,
    display: 'flex',
    alignItems: 'center',
    minHeight: 56,
}

const QUESTION_IMAGE_DIALOG_CLOSE_SX = {
    position: 'absolute',
    right: 8,
    top: 8,
}

const QUESTION_IMAGE_DIALOG_CONTENT_SX = {
    p: 0,
    bgcolor: 'background.default',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}

const DETAIL_SUMMARY_SX = {
    p: 2,
    mb: 1.5,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.paper',
    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
}

const DETAIL_SUMMARY_GRID_SX = {
    display: 'grid',
    gridTemplateColumns: {
        xs: '1fr',
        md: 'minmax(0, 1fr) minmax(240px, 0.7fr)',
    },
    gap: 2,
    alignItems: 'start',
}

const DETAIL_META_GRID_SX = {
    mt: 1.5,
    display: 'grid',
    gridTemplateColumns: {
        xs: '1fr',
        sm: 'repeat(2, minmax(0, 1fr))',
        md: 'repeat(4, minmax(0, 1fr))',
    },
    gap: 1,
}

const DETAIL_META_ITEM_SX = {
    minWidth: 0,
    p: 1,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.default',
}

const QUESTION_CONTENT_CARD_SX = {
    minWidth: 0,
    p: 2,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.default',
}

const ANSWER_SECTION_SX = {
    ...FORM_PANEL_SX,
    p: 2,
    mb: 0,
    boxShadow: 'none',
}

const ANSWER_TEXT_FIELD_SX = {
    '& .MuiInputBase-root': {
        alignItems: 'flex-start',
    },
}

const statusBadgeSx = ({ active, danger = false }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 28,
    px: 1.25,
    borderRadius: '999px',
    fontSize: 12,
    fontWeight: 700,
    color: active ? 'primary.dark' : danger ? 'error.dark' : 'text.secondary',
    backgroundColor: active ? 'action.selected' : danger ? 'error.light' : 'divider',
})

// 상세 조회 실패 시에도 답변 폼 상태를 예측 가능하게 되돌린다.
const createDefaultParams = (qnaId) => ({
    qnaId,
    userId: '',
    type: 1,
    email: '',
    content: '',
    status: 0,
})

// 문의 첨부 이미지를 고정 폭 카드 대신 화면 폭에 맞는 확인 패널로 보여준다.
function QuestionImagePreview({ imageSrc, description }) {
    const [openImageDialog, setOpenImageDialog] = useState(false)

    return (
        <>
            <Box sx={QUESTION_IMAGE_PANEL_SX}>
                <Box sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        첨부 이미지
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {description}
                    </Typography>
                </Box>
                <Box
                    component="button"
                    type="button"
                    sx={QUESTION_IMAGE_PREVIEW_BUTTON_SX}
                    onClick={() => setOpenImageDialog(true)}
                    aria-label="첨부 이미지 크게 보기"
                >
                    <Box
                        component="img"
                        src={imageSrc}
                        alt="문의 첨부 이미지"
                        sx={{
                            width: '100%',
                            maxHeight: 520,
                            objectFit: 'contain',
                            display: 'block',
                        }}
                    />
                </Box>
            </Box>
            <Dialog open={openImageDialog} onClose={() => setOpenImageDialog(false)} maxWidth="lg" fullWidth>
                <DialogTitle sx={QUESTION_IMAGE_DIALOG_TITLE_SX}>
                    첨부 이미지
                    <IconButton
                        aria-label="닫기"
                        onClick={() => setOpenImageDialog(false)}
                        sx={QUESTION_IMAGE_DIALOG_CLOSE_SX}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={QUESTION_IMAGE_DIALOG_CONTENT_SX}>
                    <Box
                        component="img"
                        src={imageSrc}
                        alt="문의 첨부 이미지 크게 보기"
                        sx={{
                            width: '100%',
                            maxHeight: 'calc(100vh - 160px)',
                            objectFit: 'contain',
                            display: 'block',
                        }}
                    />
                </DialogContent>
            </Dialog>
        </>
    )
}

function StatusBadge({ active, danger, children }) {
    return (
        <Box component="span" sx={statusBadgeSx({ active, danger })}>
            {children}
        </Box>
    )
}

function MetaItem({ label, value, onClick, clickable = false }) {
    return (
        <Box
            sx={{
                ...DETAIL_META_ITEM_SX,
                cursor: clickable ? 'pointer' : 'default',
                '&:hover': clickable ? {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover',
                } : undefined,
            }}
            onClick={onClick}
        >
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 700 }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: 'text.primary', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {value || '-'}
            </Typography>
        </Box>
    )
}

function QuestionContentCard({ content, onClick }) {
    return (
        <Box
            sx={{
                ...QUESTION_CONTENT_CARD_SX,
                cursor: 'pointer',
                '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover',
                },
            }}
            onClick={onClick}
        >
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 700 }}>
                내용
            </Typography>
            <Typography component="div" variant="body2" sx={{ mt: 0.75, color: 'text.primary', fontWeight: 700, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {content || '-'}
            </Typography>
        </Box>
    )
}

const getQnaTypeLabel = (type) => {
    switch (Number(type)) {
        case 1:
            return '일반문의'
        case 2:
            return '기능개선'
        case 3:
            return '버그신고'
        case 4:
            return '광고문의'
        default:
            return '문의유형 미설정'
    }
}

function AppQnaSummary({ params, user, onCopyMeta }) {
    const isAnswered = Number(params.status) === 1
    const qnaTypeLabel = getQnaTypeLabel(params.type)

    return (
        <Box sx={DETAIL_SUMMARY_SX}>
            <Box sx={DETAIL_SUMMARY_GRID_SX}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                        문의 내용을 확인하고 답변을 작성하세요.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 0.75, flexWrap: 'wrap' }}>
                    <StatusBadge active={!isAnswered} danger={!isAnswered}>{isAnswered ? '답변완료' : '미답변'}</StatusBadge>
                    <StatusBadge active>{qnaTypeLabel}</StatusBadge>
                    {params.image && <StatusBadge active>첨부 이미지</StatusBadge>}
                </Box>
            </Box>
            <Box sx={DETAIL_META_GRID_SX}>
                <MetaItem label="이름" value={user.username} onClick={() => onCopyMeta('이름', user.username)} clickable />
                <MetaItem label="닉네임" value={user.nickName} onClick={() => onCopyMeta('닉네임', user.nickName)} clickable />
                <MetaItem label="userId" value={params.userId} onClick={() => onCopyMeta('userId', params.userId)} clickable />
                <MetaItem label="이메일" value={params.email} onClick={() => onCopyMeta('이메일', params.email)} clickable />
            </Box>
        </Box>
    )
}

export default function AppQna(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.qnaId
    const curPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || {
        searchDeviceType: '',
        searchType: 0,
        searchStatus: 99,
        searchUserId: '',
        searchUserLabel: '',
    }
    const listSearch = location.search || ''
    const draftKey = createEditAppQnaDraftKey(id)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [hasDetail, setHasDetail] = useState(false)
    const [draftReady, setDraftReady] = useState(false)
    const [params, setParams] = useState(() => createDefaultParams(id))
    const [user, setUser] = useState({
        username: '',
        nickName: ''
    })
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const initialDraftSnapshotRef = useRef('')
    
    useEffect(() => {
        const getData = async () => {
            if (!id) {
                const errorMessage = '필수 파라미터가 없어 App 문의 상세를 불러올 수 없습니다.'
                setParams(createDefaultParams(id))
                setUser({ username: '', nickName: '' })
                setHasDetail(false)
                setDraftReady(false)
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
                setLoading(false)
                return
            }

            setLoading(true)
            setHasDetail(false)
            setDraftReady(false)
            setDetailErrorMessage('')

            try {
                const { data } = await API.post('/api/v1/admin/appQna/getDetail', { qnaId: id })
                // console.log('data: ', data.data )
                if (data.code === 200) {
                    const baseParams = data.data || {}

                    if (!baseParams.qnaId) {
                        setParams(createDefaultParams(id))
                        setUser({ username: '', nickName: '' })
                        setDetailErrorMessage('App 문의 상세 정보를 찾을 수 없습니다.')
                        setLoading(false)
                        return
                    }

                    const storedDraft = readAppQnaDraft(draftKey)
                    const restoredDraft = applyAppQnaDraft({
                        baseParams,
                        draft: storedDraft,
                    })

                    initialDraftSnapshotRef.current = JSON.stringify(createAppQnaDraftPayload({
                        params: baseParams,
                    }))

                    setParams(restoredDraft.params)
                    setUser(baseParams.user || { username: '', nickName: '' })
                    setHasDetail(Boolean(baseParams?.qnaId))
                    setDetailErrorMessage('')
                    setDraftReady(true)
                } else if (data.code === 403) {
                    setLoading(false)
                    history.push('/login')
                    return
                } else {
                    const errorMessage = data.message || 'App 문의 상세를 불러오지 못했습니다.'
                    setParams(createDefaultParams(id))
                    setUser({ username: '', nickName: '' })
                    setDetailErrorMessage(errorMessage)
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(errorMessage)
                }
            } catch (_error) {
                const errorMessage = 'App 문의 상세를 불러오지 못했습니다.'
                setParams(createDefaultParams(id))
                setUser({ username: '', nickName: '' })
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }

            setLoading(false)
        }
        getData()
    }, [draftKey, history, id])

    useEffect(() => {
        if (!draftReady || !params?.qnaId) {
            return
        }

        // App 문의 상세는 목록 query를 건드리지 않고 답변 draft만 sessionStorage에 보존한다.
        const nextDraft = createAppQnaDraftPayload({
            params,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAppQnaDraft(draftKey)
            return
        }

        saveAppQnaDraft(draftKey, nextDraft)
    }, [draftKey, draftReady, params])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const copyTextToClipboard = async (text) => {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text)
            return
        }

        const textArea = document.createElement('textarea')
        textArea.value = text
        textArea.setAttribute('readonly', '')
        textArea.style.position = 'absolute'
        textArea.style.left = '-9999px'
        document.body.appendChild(textArea)
        textArea.select()

        const copied = document.execCommand('copy')
        document.body.removeChild(textArea)

        if (!copied) {
            throw new Error('copy_failed')
        }
    }

    const handleCopyMeta = async (label, value) => {
        const copyValue = String(value ?? '').trim()

        if (!copyValue) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(`복사할 ${label} 값이 없습니다.`)
            return
        }

        try {
            await copyTextToClipboard(copyValue)
            setOpenSnackbar(true)
            setSeveritySnackbar('success')
            setMessageSnackbar(`${label}을 복사했습니다.`)
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(`${label}을 복사하지 못했습니다.`)
        }
    }

    const handleCopyContent = async () => {
        const content = String(params.content || '').trim()

        if (!content) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('복사할 내용이 없습니다.')
            return
        }

        try {
            await copyTextToClipboard(content)
            setOpenSnackbar(true)
            setSeveritySnackbar('success')
            setMessageSnackbar('문의 내용을 복사했습니다.')
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('문의 내용을 복사하지 못했습니다.')
        }
    }

    // 답변 저장 후에도 목록의 페이지/검색 조건이 그대로 복원되도록 복귀 state를 통일한다.
    const moveToList = (snackbarState) => {
        clearAppQnaDraft(draftKey)
        history.replace(createListLinkTarget({
            pathname: '/appQnas',
            search: listSearch,
            curPage: curPage,
            searchText: searchText,
            searchSelect: searchSelect,
            ...(snackbarState ? {
                openSnackbar: true,
                severitySnackbar: snackbarState.severity,
                messageSnackbar: snackbarState.message,
            } : {})
        }))
    }

    const handleUpdateField = (e) => {
        setParams({
            ...params,
            [e.target.name]: e.target.value
        })
    }

    const updateData = async () => {
        if (saving) {
            return
        }

        const trimmedReply = (params.reply || '').trim()

        if (!trimmedReply) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('답변을 입력하세요.')
            return
        }

        try {
            setSaving(true)
            // const { data } = await API.post('/api/v1/admin/appQna/update', { qnaId: params.qnaId, email: params.email, reply: params.reply })
            const { data } = await API.post('/api/v1/admin/appQna/update2', { qnaId: params.qnaId, email: params.email, reply: trimmedReply })

            if (data.code === 200) {
                moveToList({ severity: 'success', message: 'App 문의 답변을 저장했습니다.' })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('App 문의 답변을 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        updateData()
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="App 문의 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" path="/appQnas" search={listSearch} visible={false} searchText={searchText} searchSelect={searchSelect} curPage={curPage} onBackClick={() => clearAppQnaDraft(draftKey)} backLabel="목록으로"/>
            )}
            sx={{ mt: 2 }}
        />
    )

    if (loading) {
        return (
            <Grid item xs={12}>
                <Spiner/>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Grid>
        )
    }

    if (!hasDetail) {
        return (
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>App 문의 답변</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            </Grid>
        )
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>App 문의 답변</Title>
                <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }}>
                    <AppQnaSummary params={params} user={user} onCopyMeta={handleCopyMeta} />
                    <Grid container spacing={2} sx={{ marginTop: 0.5 }}>
                        {params.image &&
                            <Grid item xs={12}>
                                <QuestionImagePreview
                                    imageSrc={`${process.env.REACT_APP_AWS_S3_URL}/${params.image}`}
                                    description="사용자가 App 문의에 첨부한 이미지를 확인합니다."
                                />
                            </Grid>
                        }
                        <Grid item xs={12} sm={12} md={12}>
                            <QuestionContentCard content={params.content} onClick={handleCopyContent} />
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <Paper variant="outlined" sx={ANSWER_SECTION_SX}>
                                <Box sx={{ mb: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                        답변 작성
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                                        저장하면 App 문의 목록으로 돌아갑니다.
                                    </Typography>
                                </Box>
                                <FormControl fullWidth>
                                    <TextField
                                        label="답변"
                                        required
                                        id="reply"
                                        name="reply"
                                        multiline
                                        minRows={8}
                                        value={params.reply || ''}
                                        onChange={handleUpdateField}
                                        size="small"
                                        disabled={saving}
                                        placeholder="고객에게 전달할 답변을 입력하세요."
                                        sx={ANSWER_TEXT_FIELD_SX}
                                    />
                                </FormControl>
                            </Paper>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup
                        visible={true}
                        submitDisabled={saving}
                        submitLabel={saving ? '저장 중...' : '저장'}
                        backDisabled={saving}
                        onBackClick={() => moveToList()}
                    />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
